import type {
  ModelParamDefinition,
  ModelParamGroup,
  ModelParamScope,
  ModelParamScopeGroup,
  ModelParamUiSection,
} from '../types/modelParam'

export const MODEL_PARAM_GROUP_ORDER = [
  'Application Variables',
  'Applicant Variables',
  'Equifax Variables',
  'EDGE Variables',
  'EDGE Underwriting Core',
  'EDGE VOI',
  'EDGE VOI Expanded',
  'EDGE Loan',
] as const

export function getModelParamGroups(definitions: ModelParamDefinition[]): ModelParamGroup[] {
  const grouped = new Map<string, ModelParamDefinition[]>()

  definitions.forEach((definition) => {
    if (!grouped.has(definition.group)) {
      grouped.set(definition.group, [])
    }
    grouped.get(definition.group)!.push(definition)
  })

  return MODEL_PARAM_GROUP_ORDER.filter((title) => grouped.has(title)).map((title) => ({
    title,
    fields: grouped.get(title)!,
  }))
}

export function getModelParamScopeGroups(definitions: ModelParamDefinition[]): ModelParamScopeGroup[] {
  const scopes: { title: string; scope: ModelParamScope }[] = [
    { title: 'Application Params', scope: 'application' },
    { title: 'Applicant Params', scope: 'applicant' },
  ]

  return scopes.map(({ title, scope }) => {
    const scopedDefinitions = definitions.filter((definition) => definition.scope === scope)
    const groups = getModelParamGroups(scopedDefinitions)

    return {
      title,
      scope,
      groups,
      totalFields: scopedDefinitions.length,
    }
  })
}

function getSectionTotal(groups: ModelParamGroup[]): number {
  return groups.reduce((total, group) => total + group.fields.length, 0)
}

function matchesModelParamSearch(value: string, query: string): boolean {
  return (value || '').toLowerCase().includes(query)
}

function filterSectionGroups(
  sectionTitle: string,
  groups: ModelParamGroup[],
  query: string,
): ModelParamGroup[] {
  if (!query) {
    return groups
  }

  const sectionMatches = matchesModelParamSearch(sectionTitle, query)

  return groups
    .map((group) => {
      const groupMatches = sectionMatches || matchesModelParamSearch(group.title, query)
      const fields = groupMatches
        ? group.fields
        : group.fields.filter(
            (field) =>
              matchesModelParamSearch(field.key, query) ||
              matchesModelParamSearch(field.description, query),
          )

      return {
        title: group.title,
        fields,
      }
    })
    .filter((group) => group.fields.length > 0)
}

export function getModelParamUiSections(
  definitions: ModelParamDefinition[],
  searchTerm = '',
): ModelParamUiSection[] {
  const query = (searchTerm || '').trim().toLowerCase()
  const applicationGroups = getModelParamGroups(
    definitions.filter((definition) => definition.scope === 'application'),
  )
  const applicantGroups = getModelParamGroups(
    definitions.filter((definition) => definition.scope === 'applicant'),
  )

  const sections: ModelParamUiSection[] = [
    {
      id: 'application',
      title: 'Application Variables',
      scope: 'application',
      icon: 'fa-th-large',
      iconTone: 'blue',
      groups: applicationGroups,
      totalFields: getSectionTotal(applicationGroups),
    },
    {
      id: 'applicant',
      title: 'Applicant Variables',
      scope: 'applicant',
      icon: 'fa-users',
      iconTone: 'purple',
      groups: applicantGroups,
      totalFields: getSectionTotal(applicantGroups),
    },
  ]

  return sections
    .map((section) => {
      const groups = filterSectionGroups(section.title, section.groups, query)
      const totalFields = query ? getSectionTotal(groups) : section.totalFields

      return {
        ...section,
        groups,
        totalFields,
      }
    })
    .filter((section) => section.groups.length > 0)
}
